import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../store/auth-context';
import Pagination from '../UI/Pagination/Pagination';
import { getAppUserSessions, blacklistSession, AppUserSession } from '../../services/appUsers.service';
import dateFormat from 'dateformat';

const PAGE_SIZE = 20;

const AppUsersPage: React.FC = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<AppUserSession[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [blacklisting, setBlacklisting] = useState<number | null>(null);

  const usertype = authCtx.userData?.usertype;
  const token = authCtx.token || '';

  // Access guard
  useEffect(() => {
    if (usertype && usertype !== 'OWNER' && usertype !== 'INTERNALADMIN') {
      navigate('/Organisation');
    }
  }, [usertype, navigate]);

  const fetchSessions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getAppUserSessions(page, PAGE_SIZE, activeSearch, sortOrder, token);
      setSessions(res.data);
      setTotal(res.total);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }, [page, activeSearch, sortOrder, token]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearch = () => {
    setPage(1);
    setActiveSearch(searchInput);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSortToggle = () => {
    setPage(1);
    setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const handleBlacklist = async (session: AppUserSession) => {
    if (session.isBlacklisted) return;
    if (!window.confirm(`Force logout session #${session.id} for ${session.userName || session.userEmail || session.user_uuid}?`)) return;

    setBlacklisting(session.id);
    try {
      await blacklistSession(session.id, token);
      toast.success('Session blacklisted. User will be logged out on next request.');
      fetchSessions();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to blacklist session.');
    } finally {
      setBlacklisting(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return dateFormat(new Date(dateStr), 'dd mmm yyyy HH:MM:ss');
    } catch {
      return dateStr;
    }
  };

  const parseMetadata = (raw: string | null) => {
    if (!raw) return '—';
    try {
      const obj = JSON.parse(raw);
      return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ');
    } catch {
      return raw;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h4 style={{ marginBottom: '20px', fontWeight: 600 }}>App User Sessions</h4>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '320px' }}
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
          Search
        </button>
        {activeSearch && (
          <button
            className="btn btn-outline-secondary"
            onClick={() => { setSearchInput(''); setActiveSearch(''); setPage(1); }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Summary */}
      <div style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>
        {loading ? 'Loading…' : `${total} session${total !== 1 ? 's' : ''}${activeSearch ? ` matching "${activeSearch}"` : ''}`}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-bordered table-hover table-sm" style={{ fontSize: '13px', minWidth: '1100px' }}>
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr>
              <th style={{ whiteSpace: 'nowrap' }}>ID</th>
              <th style={{ whiteSpace: 'nowrap' }}>User</th>
              <th style={{ whiteSpace: 'nowrap' }}>User Type</th>
              <th
                style={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                onClick={handleSortToggle}
                title="Click to toggle sort order"
              >
                Session Start {sortOrder === 'desc' ? '▼' : '▲'}
              </th>
              <th style={{ whiteSpace: 'nowrap' }}>Session End</th>
              <th style={{ whiteSpace: 'nowrap' }}>Status</th>
              <th style={{ whiteSpace: 'nowrap' }}>Blacklisted</th>
              <th style={{ whiteSpace: 'nowrap' }}>Client IP</th>
              <th style={{ whiteSpace: 'nowrap' }}>Client Device</th>
              <th style={{ whiteSpace: 'nowrap' }}>Metadata</th>
              <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && sessions.length === 0 && (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                  No sessions found.
                </td>
              </tr>
            )}
            {sessions.map(session => (
              <tr key={session.id}>
                <td>{session.id}</td>
                <td>
                  <div style={{ fontWeight: 500 }}>{session.userName || '—'}</div>
                  <div style={{ color: '#888', fontSize: '12px' }}>{session.userEmail || session.user_uuid}</div>
                </td>
                <td>
                  <span
                    className={`badge ${session.usertype === 'USER' ? 'bg-secondary' : session.usertype === 'OWNER' ? 'bg-danger' : 'bg-primary'}`}
                    style={{ fontSize: '11px' }}
                  >
                    {session.usertype || session.user_type || '—'}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(session.session_start)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(session.session_end)}</td>
                <td>
                  <span
                    className={`badge ${session.status === 'ACTIVE' ? 'bg-success' : 'bg-secondary'}`}
                    style={{ fontSize: '11px' }}
                  >
                    {session.status}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {session.isBlacklisted ? (
                    <span className="badge bg-danger" style={{ fontSize: '11px' }}>Blacklisted</span>
                  ) : (
                    <span className="badge bg-success" style={{ fontSize: '11px' }}>Valid</span>
                  )}
                </td>
                <td style={{ fontSize: '12px' }}>{session.client_ip || '—'}</td>
                <td style={{ fontSize: '12px', maxWidth: '160px', wordBreak: 'break-word' }}>{session.client_device || '—'}</td>
                <td style={{ fontSize: '12px', maxWidth: '200px', wordBreak: 'break-word' }}>{parseMetadata(session.metadata)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleBlacklist(session)}
                    disabled={session.isBlacklisted || blacklisting === session.id}
                    title={session.isBlacklisted ? 'Already blacklisted' : 'Force logout this user'}
                  >
                    {blacklisting === session.id ? 'Loading…' : 'Force Logout'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <Pagination
            currentPage={page}
            totalCount={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p: number) => setPage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default AppUsersPage;
